import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, input, signal } from '@angular/core';
import { Attachment } from '@jms/model';
import { ResizeImagePipe } from '@jms/utils/util.pipes';
import { PreviewComponent } from '@workbuddy/ui/components/preview';
import { WorkBuddyDialogService } from '@workbuddy/ui/services';

const MAX_TILES = 8;
const FULL_TILES = 7;

/**
 * "Uploaded Photos" hover card for the board's photo counter (Figma Jobs 2744:185834). Content only —
 * the dark surface comes from `wb-dark-tooltip`, since this renders through a tooltip appended to body.
 * Clicking a thumb opens the same full-screen preview used by upload-field.
 */
@Component({
  selector: 'item-photos-card',
  template: `
    <div class="photos-card">
      <div class="card-title">Uploaded Photos</div>

      <div class="card-grid">
        @for (tile of $tiles(); track tile.id) {
          @let failed = $failed().has(tile.id);

          <button type="button" class="card-tile" [class.failed]="failed" [attr.aria-label]="tile.more ? 'Show ' + tile.more + ' more photos' : 'Preview photo'" (click)="onPreview(tile.photo, $event)">
            @if (failed) {
              <i class="fa-regular fa-image" aria-hidden="true"></i>
            } @else {
              <img [src]="tile.photo.thumbnail ? tile.photo.thumbnail : (tile.photo.path | resizeImage: 'h-150')" alt="" (error)="onImageError(tile.id)" />
            }

            @if (tile.more) {
              <span class="tile-more">+{{ tile.more }}</span>
            }
          </button>
        }
      </div>
    </div>
  `,
  styleUrls: ['./item.photos.card.component.less'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ResizeImagePipe],
  // Same local dialog service as upload-drop — not root-provided.
  providers: [WorkBuddyDialogService],
})
export class ItemPhotosCardComponent {

  private readonly dialogService = inject(WorkBuddyDialogService);

  public readonly $photos = input<Attachment[]>([], { alias: 'photos' });

  /** The overflow count includes the tile it sits on, matching AttachmentsComponent's grid limit. */
  protected readonly $tiles = computed(() => {

    const photos = this.$photos();
    const overflow = photos.length > MAX_TILES ? photos.length - FULL_TILES : 0;

    return photos
      .slice(0, MAX_TILES)
      .map((photo, index) => ({
        // `_id` is optional and an evidence array can repeat one, so the index keeps track unique.
        id: `${photo._id ?? ''}-${index}`,
        photo,
        more: index === MAX_TILES - 1 ? overflow : 0,
      }));
  });

  /** Evidence can include a video, whose `path` is not an image — a broken icon shows badly on black. */
  protected readonly $failed = signal<ReadonlySet<string>>(new Set());

  protected onImageError(id: string): void {

    this.$failed.update(failed => new Set(failed).add(id));
  }

  /** Open the shared attachment preview gallery, starting at the clicked photo. */
  protected onPreview(photo: Attachment, event: Event): void {

    // Keep the tooltip click from bubbling to the badge / grid row.
    event.preventDefault();
    event.stopPropagation();

    if (!photo) return;

    const photos = this.$photos();
    if (!photos.length) return;

    this.dialogService.open(PreviewComponent, {
      showHeader: false,
      closeOnEscape: true,
      dismissableMask: true,
      style: {
        display: 'contents',
      },
      contentStyle: {
        display: 'contents',
      },
      data: {
        attachments: photos,
        attachmentId: photo._id,
      },
    });
  }
}
